/**
 * @typedef {{
 *  needNetResource: boolean;
 *  criticalAPI: string | Array<string>;
 *  isFirstPage: boolean;
 * }} RecordPagePerfOptions
 * @typedef {{ startTime: number; responseEnd: number }} LoadingInfo
 * @typedef {(null | Array<LoadingInfo>)} LoadingInfos
 */

import * as firebase from 'firebase/app'
import urlParse from 'url-parse'
import _ from 'lodash'

import { SSR_MODE, DOWNGRADE_MODE, EXPORT_MODE, FIREBASE_ENDPOINT_NAME, PROMETHEUS_ENDPOINT_NAME, OFFLINE_MODE } from '../constants/index'
import { tryCall, safeComputeInteger, now } from "../utils";
import runtime, { isOfflineEnv } from "../utils/runtime";
import { getRouteName, getRouteConfig, getUrl } from '../utils/path';
import { getDeviceLevel } from '../utils/device';
import { checkCompat, performanceNowAvailable } from '../compatibility/index';
import readySATransport from '../sa/index';
import { transport } from '../transport';
import logger from '../logger/index'
import { startObserver, stopObserver } from '../mutationObserver'

let lastPageLeave = 0
let recordFnCalled = false
let isRouterHasChange = false
let isFirstPageRecorded = false

let recordOpts = {
  needNetResource: true,
  isFirstPage: false,
}

let finalLoadingInfo = {
  startTime: 0,
  responseEnd: 0,
}
// loading 结束后首次 mutation observe callback 被回调时间
let mutationTime = 0
let maybePaintTime = 0
let pageDidMountTime = 0
let pageEnter = 0
let eventAdded = false

// 检查数据完整性 interval
let checkDataInterval
let checkDataTimes = 0
const MAX_TIMES = 10
const DOM_COMPLETE_DELAY = 5000
const CHECK_DELAY = 1000

export function getPageMode() {
  if (isOfflineEnv()) {
    return OFFLINE_MODE
  }
  // 路由切换都认为是 csr 的情况
  if (isRouterHasChange) {
    return EXPORT_MODE
  }

  const pageMode = runtime.get('pageMode')
  if (window.AL_SSR_DOWNGRADE) {
    return DOWNGRADE_MODE
  }

  return pageMode
}

function isSSRMode() {
  return getPageMode() === SSR_MODE
}

function getMaxAPIResponseEnd(infos) {
  return Math.max(...infos.map(info => info.responseEnd))
}

function clearCheckInterval() {
  clearInterval(checkDataInterval)
  checkDataInterval = undefined
}

function resetVars() {
  recordFnCalled = false
  finalLoadingInfo = {
    startTime: 0,
    responseEnd: 0,
  }
  mutationTime = 0
  maybePaintTime = 0
  pageDidMountTime = 0
  checkDataTimes = 0
}

function isBlankReady() {
  const blank = getBlankTime()
  return typeof blank !== 'undefined'
}

function isDomComplete() {
  return performance.timing.domComplete !== 0
}

function isRenderReady() {
  return mutationTime > 0
}

function isLoadingReady(opts) {
  const { ok, infos } = checkLoadingInfosFulfilled(opts)
  return ok
}

function isDataPrepared(opts) {
  const blankReady = isBlankReady()
  // 如果是 CSR 模式，不需要使用domComplete ，直接设置为 true
  let domComplete = isSSRMode() ? isDomComplete() : true
  const renderReady = isRenderReady()
  const loadingReady = isLoadingReady(opts)
  if (opts.criticalAPI && opts.criticalAPI.length > 0) {
    return blankReady && domComplete && loadingReady && renderReady
  }

  return blankReady && domComplete
}

/**
 *
 * @param {RecordPagePerfOptions} opts
 */
function startCheckData(opts) {
  checkDataInterval = setInterval(() => {
    if (checkDataTimes >= MAX_TIMES) {
      clearCheckInterval()
      //计算关键任务
      checkLoadingInfosFulfilled(opts)
      // 多次检查后，依然没有 loading, mutation 等信息，也进行上报
      stopObserver()
      doRecord(opts)
      return false
    }

    if (isDataPrepared(opts)) {
      clearInterval(checkDataInterval)
      checkDataTimes = 0
      doRecord(opts)
    } else {
      checkDataTimes += 1
    }
  }, CHECK_DELAY)
}

/**
 *
 * @param {RecordPagePerfOptions} opts
 */
function doRecord(opts) {
  const routeName = getRouteName()

  if (isOfflineEnv()) {
    // 离线包的情况都是 CSR 渲染   
    recordCSRPagePerformance(routeName, opts, "off")
  } else {
    // 路由切换都认为是 csr 的情况
    if (isSSRMode() && !isRouterHasChange) {
      recordSSRPagePerformance(routeName, opts)
    } else {
      recordCSRPagePerformance(routeName, opts)
    }
  }
}

function getAPIResources() {
  return performance.getEntriesByType('resource').filter(r => r.initiatorType === 'xmlhttprequest' || r.initiatorType === 'fetch')
}

/**
 *
 * @param {string} url
 * 获取匹配 url 的 api performance entry
 * @return undefined | PerformanceResourceEntry
 */
function getAPIEntry(url) {
  const parsed = urlParse(url)
  const { pathname } = parsed
  const apis = getAPIResources()
  const apiEntry = apis.find(entry => {
    const p = urlParse(entry.name)
    // 前端路由切换，同一资源可能出现多次，因此判断此资源开始时间是否在页面离开之后，如果是首次加载，lastPageLeave 为 0，亦可用于此处
    return p.pathname === pathname && entry.startTime > lastPageLeave
  })

  return apiEntry
}

/**
 *
 * @param {RecordPagePerfOptions} opts
 * 获取所有关键接口的 api performance entry
 * @return null | Array<LoadingInfo>
 */
function getLoadingInfos(opts) {
  const { criticalAPI } = opts;
  if (criticalAPI && typeof criticalAPI === 'string') {
    const entry = getAPIEntry(criticalAPI)
    if (entry) {
      return [
        {
          startTime: entry.startTime,
          responseEnd: entry.responseEnd,
        }
      ]
    }

    return null
  }

  if (criticalAPI && Array.isArray(criticalAPI)) {
    const entries = criticalAPI.map(url => {
      const entry = getAPIEntry(url)
      if (entry) {
        return {
          startTime: entry.startTime,
          responseEnd: entry.responseEnd,
        }
      }

      return null
    })

    return entries
  }

  return null
}

function getTimeOrigin() {
  return isRouterHasChange ? lastPageLeave : performance.timeOrigin;
}

/**
 *
 * @param {LoadingInfos} infos
 * 记录最终的 loading 开始与结束时间
 */
function setFinalLoadingInfo(infos) {
  finalLoadingInfo = {
    startTime: Math.min(...infos.map(info => info.startTime)),
    responseEnd: getMaxAPIResponseEnd(infos)
  }
}

/**
 *
 * @param {RecordPagePerfOptions} opts
 * @return {{ ok: boolean; infos: LoadingInfos }}
 * 检查 loading 数据是否已完全获取到
 */
function checkLoadingInfosFulfilled(opts) {
  const infos = getLoadingInfos(opts)
  if (infos && !infos.includes(null)) {
    setFinalLoadingInfo(infos)
    return {
      ok: true,
      infos,
    }
  }

  return {
    ok: false,
    infos,
  }
}

/**
 *
 * @param {RecordPagePerfOptions} opts
 */
function getAttributes(opts) {
  return {
    mode: getPageMode(),
    need_net_resource: opts.needNetResource ? 'true' : 'false',
    device_level: getDeviceLevel(),
  }
}

function getBlankTime() {
  return tryCall(() => performance.getEntriesByType('paint').find(i => i.name === "first-paint").startTime)
}

function getRenderTime() {
  // loading 完成后可能也没有触发 render，比如未登录情况下，只有一个在 body 内的 toast 出现，因此 mutationTime 一直为0
  if (finalLoadingInfo.responseEnd > 0 && mutationTime === 0) {
    return 0
  }

  // performance.now 不可用时，mutationTime 是 Date.now()，是个时间戳，因此需要先减去 timeOrigin 来获取差值，再减去 responseEnd，获得 render 时间
  return performanceNowAvailable() ? mutationTime - finalLoadingInfo.responseEnd : mutationTime - performance.timeOrigin - finalLoadingInfo.responseEnd
}

function getInteger(num) {
  return safeComputeInteger(() => num)
}

function getSSRMetrics() {
  const blank = getBlankTime()
  const loading = finalLoadingInfo.responseEnd - finalLoadingInfo.startTime
  const render = getRenderTime()
  const interactive = blank + loading + render
  const ready = performance.timing.domComplete - performance.timing.navigationStart

  return {
    blank: getInteger(blank),
    loading: getInteger(loading),
    render: getInteger(render),
    interactive: getInteger(interactive),
    ready: getInteger(ready),
  }
}

function getCSRInteractiveTime({ blank, loading, render }) {
  let interactive = 0;

  // 标记为首页的取全部时间，不需要减去之前页面的离开时间
  if (recordOpts.isFirstPage && !isFirstPageRecorded) {
    if (loading === 0 && render === 0) {
      // performance.now 不可用时，pageDidMountTime 是 Date.now()，是个时间戳，差值需减去 timeOrigin 来获取
      // 当 loading 和 render 都为0（即没有关键接口时，可交互为当前页面componentDidMount时间）
      interactive = performanceNowAvailable() ? pageDidMountTime : pageDidMountTime - performance.timeOrigin
    } else if (loading !== 0 && render === 0) {
      // 有关键接口，loading 不为0，但是没有触发 render 的情况，可交互时间为关键接口请求结束时间
      interactive = finalLoadingInfo.responseEnd;
    } else {
      // 此处为 loading, render 都有值，那么可交互时间就是 render 触发时间
      // performance.now 不可用时，mutationTime 是 Date.now()，是个时间戳，差值需减去 timeOrigin 来获取
      interactive = performanceNowAvailable() ? mutationTime : mutationTime - performance.timeOrigin;
    }
  } else {
    if (loading === 0 && render === 0) {
      // 当 loading 和 render 都为0（即没有关键接口时，可交互为 首次渲染时间）
      interactive = blank;
    } else if (loading !== 0 && render === 0) {
      // 有关键接口，loading 不为0，但是没有触发 render 的情况，可交互时间为关键接口请求结束时间减去上个页面离开时间
      // performance.now 不可用时，lastPageLeave 是 Date.now()，是个时间戳，差值需减去 timeOrigin 来获取
      interactive = isRouterHasChange
        ? finalLoadingInfo.responseEnd -
        (performanceNowAvailable() ? lastPageLeave : lastPageLeave - performance.timeOrigin)
        : finalLoadingInfo.responseEnd;
    } else {
      // 此处为 loading, render 都有值，那么可交互时间就是 render 触发时间
      // performance.now 不可用时，mutationTime 是 Date.now()，是个时间戳，差值需减去 timeOrigin 来获取
      // interactive = performanceNowAvailable() ? mutationTime : mutationTime - getTimeOrigin()
      interactive = performanceNowAvailable()
        ? mutationTime - (isRouterHasChange ? lastPageLeave : 0)
        : mutationTime - getTimeOrigin();
    }
  }
  return interactive;
}

function getCSRMetrics() {
  let blank = getBlankTime()
  if (isRouterHasChange) {
    blank = maybePaintTime - lastPageLeave
  }

  const loading = finalLoadingInfo.responseEnd - finalLoadingInfo.startTime
  const render = getRenderTime()
  let interactive = getCSRInteractiveTime({ blank, loading, render });


  return {
    blank: getInteger(blank),
    loading: getInteger(loading),
    render: getInteger(render),
    interactive: getInteger(interactive),
  }
}

function pageMetricsValidator(value) {
  return value >= 0
}

/**
 *
 * @param {string} routeName
 * @param {RecordPagePerfOptions} opts
 */
function recordSSRPagePerformance(routeName, opts) {
  const metrics = getSSRMetrics()
  const attributes = getAttributes(opts)

  recordFnCalled = transport({
    endPoints: [FIREBASE_ENDPOINT_NAME, PROMETHEUS_ENDPOINT_NAME],
    type: 'page_perf',
    traceName: `s_${routeName}`,
    url: getUrl(),
    message: {
      attributes,
      metrics: {
        duration: metrics.interactive,
        ..._.pick(metrics, ['blank', 'ready']),
      },
      metricsPredicate: pageMetricsValidator,
    },
  });
}

/**
 *
 * @param {string} routeName
 * @param {RecordPagePerfOptions} opts
 */
function recordCSRPagePerformance(routeName, opts, prefix) {
  const metrics = getCSRMetrics()
  const attributes = getAttributes(opts)

  recordFnCalled = transport({
    endPoints: [FIREBASE_ENDPOINT_NAME, PROMETHEUS_ENDPOINT_NAME],
    type: prefix ? `${prefix}_page_perf` : 'page_perf',
    traceName: `${prefix || 'c'}_${routeName}`,
    url: getUrl(),
    message: {
      attributes: {
        ...attributes,
        is_route_change: isRouterHasChange ? 'true' : 'false',
        is_first_page: opts.isFirstPage && !isFirstPageRecorded ? 'true' : 'false',
      },
      metrics: {
        duration: metrics.interactive,
        ..._.pick(metrics, ['blank', 'loading', 'render']),
      },
      metricsPredicate: pageMetricsValidator,
    },
  });
  // 只记录一个首页
  if (opts.isFirstPage && !isFirstPageRecorded) {
    isFirstPageRecorded = true
  }
}

// 页面切换 清空 recordFnCalled
export function whenRouterChange() {
  // API 兼容性检查
  if (!checkCompat()) {
      return false
  }
  lastPageLeave = now()
  isRouterHasChange = true

  // checkDataInterval 存在表示上个页面的数据检查没有结束
  if (recordFnCalled || checkDataInterval) {
    // 上个页面 api 未完全加载的话，mutation observer 没有停止，先停止
    stopObserver()
    // 正常情况下，observer 已被 disconnect，此时重新开始监听
    startObserver(observeCallback)
  } else {
    logger.warn('recordPagePerformance 没有触发！')
  }

  // 路由切换后，如果上一个页面的数据还未获取到，直接丢弃上次上报
  if (checkDataInterval) {
    clearCheckInterval()
  }
  resetVars()

  onLeavePage(recordOpts)
}

function recordPageData(opts) {
  pageEnter = now()
  if (!eventAdded) {
    addPageEvent(opts)
  }
}

function addPageEvent(opts) {
  window.addEventListener('beforeunload', () => onLeavePage(opts))
  document.addEventListener("visibilitychange", () => onVisibleChange(opts))
  eventAdded = true
}

function onVisibleChange(opts) {
  if (document.visibilityState === 'visible') {
    pageEnter = now()
  } else {
    onLeavePage(opts)
  }
}

function onLeavePage(opts) {
  const n = now()
  const stayTime = n - pageEnter
  const config = getRouteConfig()
  let metrics
  if (config.sa) {
    // 计算关键任务
    checkLoadingInfosFulfilled(opts)

    if (isOfflineEnv()) {
      metrics = getCSRMetrics();
    } else {
      if (isSSRMode() && !isRouterHasChange) {
        metrics = getSSRMetrics()
      } else {
        metrics = getCSRMetrics()
      }
    }
    
    let params = config.sa.params;
    if (opts.saParams && typeof opts.saParams === "function") {
      params = {
        ...params,
        ...opts.saParams(),
      }
    }

    if (!params || (params && Object.keys(params).length === 0)) {
      logger.error('[performance-collect] 神策埋点配置缺失')
      return false
    }

    readySATransport({
      ...params,
      event: 'WebViewScreen',
      Page_stayTime: getInteger(stayTime),
      Page_loadTime: metrics.interactive,
    }, pageMetricsValidator)
  }
}

export function observeCallback(mutationsList, ob) {
  const n = now()
  const { ok, infos } = checkLoadingInfosFulfilled(recordOpts)
  if (ok && n > getMaxAPIResponseEnd(infos)) {
    mutationTime = n
    stopObserver()
  }
}

/**
 *
 * @param {RecordPagePerfOptions} opts
 */
export function recordPagePerformance(opts) {
    // API 兼容性检查
    if (!checkCompat()) {
        return false
    }

  if (recordFnCalled) {
    logger.warn('[performance-collect] recordPagePerformance 已经触发过了')
    return false
  }

  // 路由切换的情况下，业务在 componentDidMount 内调用此函数的时间，即是白屏结束的时间
  if (maybePaintTime === 0) {
    maybePaintTime = now()
  }

  pageDidMountTime = now()

  recordOpts = opts
  const currentRouteConfig = getRouteConfig()

  if (currentRouteConfig.firebase && currentRouteConfig.firebase.enabled) {
    if (!isDataPrepared(opts)) {
      startCheckData(opts)
    } else {
      doRecord(opts)
    }
  }

  if (currentRouteConfig.sa && currentRouteConfig.sa.enabled) {
    recordPageData(opts)
  }
}
